import { useEffect, useState } from "react";
import api from "../../api/client";
import NavBar from "../../components/NavBar";
import LoadingSpinner from "../../components/LoadingSpinner";

function formatPostedDate(createdAt) {
  const posted = new Date(createdAt);
  const now = new Date();
  const days = Math.floor((now - posted) / (1000 * 60 * 60 * 24));

  if (days === 0) return "Posted today";
  if (days === 1) return "Posted yesterday";
  return `Posted ${days} days ago`;
}

function formatClosingDate(closesAt) {
  if (!closesAt) return null;
  const close = new Date(closesAt);
  const now = new Date();
  const days = Math.floor((close - now) / (1000 * 60 * 60 * 24));

  if (days < 0) return null; // filtered server-side already, just a safety net
  if (days === 0) return { text: "Closes today", tone: "amber" };
  if (days <= 3) return { text: `Closes in ${days}d`, tone: "amber" };
  return {
    text: `Closes ${close.toLocaleDateString("en-SE", {
      month: "short",
      day: "numeric",
    })}`,
    tone: "neutral",
  };
}

function PositionCard({ position, onApply, isApplying, isApplied }) {
  const [expanded, setExpanded] = useState(false);
  const closing = formatClosingDate(position.closes_at);

  const locationParts = [
    position.location_city,
    position.location_region,
    position.location_country,
    position.location_global_area,
  ].filter(Boolean);

  return (
    <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm dark:shadow-none overflow-hidden transition-shadow hover:shadow-md">
      {/* ── Accordion header ── */}
      <div
        className="flex items-center gap-4 px-5 py-4 cursor-pointer select-none"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="w-10 h-10 rounded-xl bg-brand-violet/20 flex items-center justify-center text-brand-violet dark:text-brand-violet-on-dark font-bold text-lg flex-shrink-0">
          {position.posted_by_name[0].toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm truncate">
              {position.title}
            </span>
            <span className="text-gray-400 text-sm">·</span>
            <span className="text-gray-500 dark:text-gray-400 text-sm truncate">
              {position.posted_by_name}
              {position.posted_by_is_agency ? " (agency)" : ""}
            </span>
            {locationParts.length > 0 && (
              <>
                <span className="text-gray-400 text-sm">·</span>
                <span className="text-gray-400 text-xs">
                  {locationParts.join(", ")}
                </span>
              </>
            )}
            {position.is_remote && (
              <span className="text-xs bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">
                Remote
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400">
              {position.employment_type}
            </span>
            <span className="text-xs text-gray-400">
              {position.application_count === 0
                ? "No applicants yet"
                : `${position.application_count} applicant${position.application_count !== 1 ? "s" : ""}`}
            </span>
            <span className="text-xs text-gray-400">
              {formatPostedDate(position.created_at)}
            </span>
            {closing && (
              <span
                className={`text-xs ${
                  closing.tone === "amber"
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-gray-400"
                }`}
              >
                {closing.text}
              </span>
            )}
            {position.salary_min && (
              <span className="text-xs text-gray-400">
                {position.salary_min.toLocaleString()}
                {position.salary_max
                  ? `–${position.salary_max.toLocaleString()}`
                  : "+"}{" "}
                {position.salary_currency}
              </span>
            )}
          </div>
        </div>

        <div
          className="flex items-center gap-2 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onApply(position)}
            disabled={isApplying || isApplied}
            className="bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isApplied ? "Applied" : isApplying ? "Applying..." : "Apply"}
          </button>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* ── Expanded content ── */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-white/10 p-5">
          {position.description ? (
            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
              {position.description}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">
              No description provided.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Heuristic country → region mapping. location_country is free text
// typed by the employer, not a fixed list, so this can't be exhaustive
// or guaranteed accurate — it's a best-effort inference layer so a
// region filter still surfaces positions where the employer only
// typed a country and skipped the global_area dropdown.
const COUNTRY_TO_REGIONS = {
  sweden: ["Nordics", "EMEA"],
  norway: ["Nordics", "EMEA"],
  denmark: ["Nordics", "EMEA"],
  finland: ["Nordics", "EMEA"],
  iceland: ["Nordics", "EMEA"],
  germany: ["DACH", "EMEA"],
  austria: ["DACH", "EMEA"],
  switzerland: ["DACH", "EMEA"],
  "united kingdom": ["EMEA"],
  uk: ["EMEA"],
  ireland: ["EMEA"],
  france: ["EMEA"],
  spain: ["EMEA"],
  portugal: ["EMEA"],
  italy: ["EMEA"],
  netherlands: ["EMEA"],
  belgium: ["EMEA"],
  poland: ["CEE", "EMEA"],
  "czech republic": ["CEE", "EMEA"],
  hungary: ["CEE", "EMEA"],
  romania: ["CEE", "EMEA"],
  "united arab emirates": ["MENA", "EMEA"],
  uae: ["MENA", "EMEA"],
  "saudi arabia": ["MENA", "EMEA"],
  egypt: ["MENA", "EMEA"],
  israel: ["MENA", "EMEA"],
  "united states": ["AMER"],
  usa: ["AMER"],
  us: ["AMER"],
  canada: ["AMER"],
  mexico: ["AMER", "LATAM"],
  brazil: ["LATAM", "AMER"],
  argentina: ["LATAM", "AMER"],
  colombia: ["LATAM", "AMER"],
  chile: ["LATAM", "AMER"],
  india: ["APAC"],
  philippines: ["APAC", "SEA"],
  singapore: ["APAC", "SEA"],
  malaysia: ["APAC", "SEA"],
  indonesia: ["APAC", "SEA"],
  thailand: ["APAC", "SEA"],
  vietnam: ["APAC", "SEA"],
  japan: ["APAC"],
  "south korea": ["APAC"],
  china: ["APAC"],
  australia: ["APAC", "ANZ"],
  "new zealand": ["APAC", "ANZ"],
};

export default function Arena() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(new Set());
  const [applied, setApplied] = useState(new Set());
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [sortBy, setSortBy] = useState("newest");
    const [onlyUnapplied, setOnlyUnapplied] = useState(false);

  useEffect(() => {
    document.title = "Joborigo — Arena";
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPositions() {
      try {
        const { data } = await api.get("/arena/positions");
        if (!cancelled) {
          setPositions(data);
          setApplied(
            new Set(
              data
                .filter((p) => p.already_applied)
                .map((p) => p.submission_token),
            ),
          );
        }
      } catch {
        if (!cancelled) setError("Could not load positions right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPositions();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredPositions = positions
    .filter((p) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        p.posted_by_name.toLowerCase().includes(q)
      );
    })
    .filter((p) => {
      const loc = locationFilter.trim().toLowerCase();
      if (!loc) return true;
      const positionLocation = [
        p.location_city,
        p.location_region,
        p.location_country,
        p.location_global_area,
        p.is_remote ? "remote" : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return positionLocation.includes(loc);
    })
    .filter((p) => {
      if (!regionFilter) return true;
      if (p.location_global_area === regionFilter) return true;
      const country = (p.location_country || "").trim().toLowerCase();
      const inferred = COUNTRY_TO_REGIONS[country] || [];
      return inferred.includes(regionFilter);
    })
    .filter((p) => {
      if (!onlyUnapplied) return true;
      return !applied.has(p.submission_token);
    })
    .sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (sortBy === "least_applied") {
        return a.application_count - b.application_count;
      }
      if (sortBy === "most_applied") {
        return b.application_count - a.application_count;
      }
      return 0;
    });

  async function handleApply(position) {
    const token = position.submission_token;
    setApplying((prev) => new Set(prev).add(token));

    try {
      await api.post(`/job-positions/by-token/${token}/submit`, { source: "arena" });
      setApplied((prev) => new Set(prev).add(token));
    } catch {
      setError("Could not submit your application. Please try again.");
    } finally {
      setApplying((prev) => {
        const next = new Set(prev);
        next.delete(token);
        return next;
      });
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <NavBar currentPage="arena" />

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Arena</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              {positions.length} open position
              {positions.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 w-full">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or company..."
            className="flex-1 border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
          />
          <input
            type="text"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            placeholder="Location or remote..."
            className="flex-1 border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
          />
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="flex-1 border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
          >
            <option value="">Any region</option>
            <option value="EMEA">EMEA</option>
            <option value="APAC">APAC</option>
            <option value="AMER">AMER</option>
            <option value="LATAM">LATAM</option>
            <option value="DACH">DACH</option>
            <option value="Nordics">Nordics</option>
            <option value="CEE">CEE</option>
            <option value="MENA">MENA</option>
            <option value="SEA">SEA</option>
            <option value="ANZ">ANZ</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-surface rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
          >
            <option value="newest">Newest</option>
            <option value="least_applied">Least applied for</option>
            <option value="most_applied">Most applied for</option>
          </select>
        </div>

        <div className="mb-6 ml-1">
          <label className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyUnapplied}
              onChange={(e) => setOnlyUnapplied(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-brand-violet focus:ring-brand-violet"
            />
            Only show positions I haven't applied to
          </label>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 text-red-600 dark:text-red-400 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <LoadingSpinner />
          </div>
        ) : filteredPositions.length === 0 ? (
          <div className="text-center text-gray-400 py-20">
            {search
              ? "No positions match your search."
              : "No open positions right now."}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredPositions.map((p) => (
              <PositionCard
                key={p.id}
                position={p}
                onApply={handleApply}
                isApplying={applying.has(p.submission_token)}
                isApplied={applied.has(p.submission_token)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
